/**
 * #328 Phase 2 — operator-facing copy for the two-way inter-ops information-sharing thread
 * (the roll-up pane, the composer, and the per-recipient breakdown). Pinned by
 * interopsVocabulary.lock.test.ts (presence + forbidden + honesty).
 *
 * Doctrine bindings (founder-ratified 2026-07-05; validated vs LIVE ontario.ca / PERP):
 *  - the roll-up pane is "Information sharing" (§2.2 the Liaison's coordination surface).
 *  - "Request assistance" is the verb toward ministries/federal — the ministry decides
 *    deployment (PERP §6.7.1); the message KIND spells out "Request for assistance (RFA)"
 *    (PERP §6.7.2). "Task" stays the agency_tasks verb (council A.7) — NOT used here.
 *  - the federal leg is recorded to Public Safety Canada; the Government Operations Centre
 *    (GOC) appears ONLY as a caption inside the RFA record — NEVER a target (R1 / §6.7.2).
 *  - an out-of-app leg is "Sent" — never proven read/acknowledged (R3 / A.6). The honesty
 *    note is reused verbatim from the #328 Phase 1 briefing surface, not reinvented.
 *  - "EOC / Emergency Operations Centre" is spelled out (App A #25 / A.5); never "Ops centre".
 *  - an acknowledgement is a RECEIPT (A.6) — never "own"/"owned"; this is not a chat/DM.
 *  - #567 — a reply received OUT OF APP is RECORDED (App A #72 Scribe); the chrome names the
 *    external party as the SOURCE and the operator as the recorder ("recorded from outside the
 *    COP"). It never implies the external party used the COP, and never fabricates a read/ack on
 *    the original "Sent" leg (that state is unchanged — R3 / A.6).
 */

// The honesty note (Sent ≠ read) is REUSED verbatim from the briefing surface (do not invent
// a "Delivered ✓"). The FN co-notify caption is reused verbatim from the PEOC-arrivals surface.
export { BRIEF_OFFICIAL_SENT_NOT_ACK_NOTE } from './incidentVocabulary';
export { ISC_ALSO_NOTIFIED_LABEL, ACKNOWLEDGE_RECEIPT_LABEL } from './peocArrivalsVocabulary';

import type { InteropsDeliveryChannel, InteropsMessageKind } from '@tsr/shared-types';

/* ── Pane / thread heading (§2.2) ─────────────────────────────────────────── */

/** The Coordination roll-up pane + the incident-surface thread heading. */
export const INFORMATION_SHARING_LABEL = 'Information sharing';
export const INFORMATION_SHARING_SUBTEXT =
  'Two-way messages and requests shared with other Emergency Operations Centres, Indigenous Services Canada, and federal partners.';
export const INFORMATION_SHARING_EMPTY = 'No information shared yet.';

/* ── Standalone (non-incident) thread section (#552) ──────────────────────── */

/** The Coordination section header for an inter-ops thread NOT attached to any incident
 *  (context_type 'standalone'). Sits beside the per-incident roll-up (#552). */
export const STANDALONE_THREAD_LABEL = 'Not attached to an incident';
export const STANDALONE_THREAD_SUBTEXT =
  'Share information or request assistance not tied to a tracked incident.';

/* ── Onward-leg read-back (RBAC Slice 3c — the ORIGINATOR's read-only view) ─── */

/** The section header on the read-only mount a municipality / First Nation watches to see how
 *  the PEOC is handling THE request it raised — the recorded onward legs (RFA to Public Safety
 *  Canada / a ministry) + recorded responses. DESCRIPTIVE-NO-COLLISION (gate-ruled 2026-07-06):
 *  describes the WORK/leg, never a party — no ownership word, no US-ICS term, not a chat. */
export const ONWARD_HANDLING_LABEL = 'Onward handling';
/** A recorded off-app reply, labelled party-neutrally in the read-only view — describes the WORK
 *  (a response was recorded at HH:MM), never attaching observation language to a party (FN-dignity;
 *  the PEOC work-surface keeps the fuller `recordedResponseChromeLabel`). */
export const RESPONSE_RECORDED_LABEL = 'Response recorded';
/** "Response recorded · HH:MM" (mirrors `acknowledgedAtLabel`). */
export function responseRecordedAtLabel(hhmm: string): string {
  return `${RESPONSE_RECORDED_LABEL} · ${hhmm}`;
}

/* ── Message kinds (spell RFA out on first use — §4.1) ────────────────────── */

export const INFORMATION_KIND_LABEL = 'Information';
export const RFA_KIND_LABEL = 'Request for assistance (RFA)';
/** #567 — a pasted-back reply from an out-of-app leg, joined to the thread as a record. */
export const RECORDED_RESPONSE_KIND_LABEL = 'Recorded response';

export function interopsKindLabel(kind: InteropsMessageKind): string {
  if (kind === 'request_for_assistance') return RFA_KIND_LABEL;
  if (kind === 'recorded_response') return RECORDED_RESPONSE_KIND_LABEL;
  return INFORMATION_KIND_LABEL;
}

/* ── Composer ─────────────────────────────────────────────────────────────── */

export const SEND_LABEL = 'Send';
export const SENDING_LABEL = 'Sending…';
/** The verb toward ministries/federal (PERP §6.7.1 — the ministry decides deployment). */
export const REQUEST_ASSISTANCE_LABEL = 'Request assistance';
export const COMPOSER_RECIPIENTS_LABEL = 'Recipients';
export const COMPOSER_PLACEHOLDER = 'Write a message to share…';
export const COMPOSER_KIND_LABEL = 'Kind';
/** The FN one-act preset (PEOC + ISC in one send; PERP §6.7.1). */
export const FN_PRESET_LABEL = 'Notify PEOC + ISC';

/* ── Directed row conventions (A.2) + author byline (#452 / A.5) ──────────── */

/** INBOUND row prefix (A.2): "From <originator>". */
export function inboundFromLabel(name: string): string {
  return `From ${name}`;
}
/** OUTBOUND row prefix (A.2): "To <target>" (generic fallback when a name is absent). */
export function outboundToLabel(name: string | null | undefined): string {
  return `To ${name ?? 'recipient'}`;
}
/** The #452 / A.5 author byline: "From <desk> — to <audience>". */
export function authorBylineLabel(desk: string, audience: string): string {
  return `From ${desk} — to ${audience}`;
}

/* ── Per-recipient state (RecipientBreakdown) ─────────────────────────────── */

/** In-app recipient, delivered to its COP pane: "Received by <desk>". */
export function receivedByLabel(desk: string): string {
  return `Received by ${desk}`;
}
export const AWAITING_ACK_LABEL = 'Not yet acknowledged';
export const ACKNOWLEDGED_LABEL = 'Acknowledged';
/** In-app recipient acknowledged: "Acknowledged · HH:MM". */
export function acknowledgedAtLabel(hhmm: string): string {
  return `${ACKNOWLEDGED_LABEL} · ${hhmm}`;
}
/** Out-of-app leg: "Sent HH:MM" (honesty: NOT proof it was read — see the reused note). */
export function sentAtLabel(hhmm: string): string {
  return `Sent ${hhmm}`;
}

/* ── Record the out-of-app response (#567 — App A #72 Scribe) ──────────────── */

/** The affordance on an out-of-app leg + the dialog's confirm button. */
export const RECORD_RESPONSE_LABEL = 'Record response';
/** The paste-dialog title + hint + placeholder + cancel. HONESTY: the dialog is explicit that
 *  this records a reply received OUTSIDE the COP — it never implies the external party used it. */
export const RECORD_RESPONSE_DIALOG_TITLE = 'Record the response received outside the COP';
export const RECORD_RESPONSE_DIALOG_HINT =
  'Paste the reply exactly as received. It joins this thread as a recorded document — a record, not a message sent to them.';
export const RECORD_RESPONSE_PLACEHOLDER = 'Paste the reply received outside the COP…';
export const RECORD_RESPONSE_CANCEL_LABEL = 'Cancel';
/** Sub-label under a recorded reply card — plain about its provenance (never a read/ack claim). */
export const RECORDED_FROM_OUTSIDE_COP_SUBLABEL = 'Recorded from outside the COP';
/** Null-data fallbacks for the chrome + dialog (kept HERE so the vocabulary lock covers them —
 *  a lock is useless if a rendered string is hard-coded outside V.*). */
export const RECORDED_SOURCE_FALLBACK = 'the external partner';
export const RECORDING_AGENCY_FALLBACK = 'the COP';
/** The recorded-reply card chrome: "Response from <destination> — recorded HH:MM by <operator
 *  agency>". Names the external party as the SOURCE of the reply and the operator as the recorder
 *  — never implies the external party acted in the COP, never an acknowledgement of the sent leg. */
export function recordedResponseChromeLabel(
  destinationName: string,
  hhmm: string,
  recordedByAgency: string,
): string {
  return `Response from ${destinationName} — recorded ${hhmm} by ${recordedByAgency}`;
}

/** Delivery-channel pill label (NEVER a raw enum). */
export function channelPillLabel(channel: InteropsDeliveryChannel): string {
  switch (channel) {
    case 'in_app':
      return 'In-app';
    case 'webhook':
      return 'Webhook';
    case 'email':
      return 'Email';
    case 'console_log':
      return 'Recorded';
  }
}

/* ── Federal RFA caption (GOC is a CAPTION, never a target — R1 / §6.7.2) ──── */

/** Shown inside an RFA record addressed to Public Safety Canada. The Government Operations
 *  Centre (GOC) appears ONLY here, as a description of the federal routing — never as an
 *  addressable destination (verbatim from PERP §6.7.2). */
export const RFA_FEDERAL_CAPTION =
  'Public Safety Canada liaises with the Government Operations Centre (GOC) to expedite the federal response.';
